import "./sentry";
import React from "react";

import { toast, ToastContainer } from "react-toastify";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import LogRocket from "logrocket";

import "./assets/styles/index.css";
import "react-toastify/dist/ReactToastify.css";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";

import "@services/axios";
import "@services/i18n";
import "./lib/dayjsConfig";

import * as serviceWorker from "./serviceWorker";
import App from "./App";
import { ThemeProviderWrapper } from "./theme";
import { ValidationError } from "yup";
import { useAppTranslation } from "@hooks";
import { createRoot } from "react-dom/client";
import { TourTriggerContextProvider } from "@context";
import { ALLOWEAT_API_ERROR, ALLOWEAT_API_SERVER_ERROR } from "@utils";
import { VisibleNutrientsContextProvider } from "@context/VisibleNutrientsContext";
import { AxiosError } from "axios";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import CheckInCircleIcon from "@icons/checkInCircle.svg?react";
import ExclamationMarkInCircleIcon from "@icons/exclamationMarkInCircle.svg?react";
import ExclamationMarkInTriangleIcon from "@icons/exclamationMarkInTriangle.svg?react";
import IInCircle from "@icons/iInCircle.svg?react";
import { VersionToastInfo } from "@components/VersionToastInfo";
import { BlockedModal } from "@components/BlockedModal";
import { ENVIRONMENT } from "@config/app";

// DON'T DELETE!!!! For libraries that use the global object, even though it doesn't exist in the browser
if (typeof window !== "undefined") {
  window.global = window;
}

const ErrWarn = () => {
  const { t } = useAppTranslation();

  return <span>{t("errors.unexpected")}</span>;
};
const LogoutWarn = () => {
  const { t } = useAppTranslation();

  return <span>{t("common.session_expired")}</span>;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000,
      refetchOnWindowFocus: false,
      onError: (e: any) => {
        console.log(e);

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (!window.subscriptionOK) return;

        if (
          typeof e?.response?.status === "number" &&
          e.response.status === 403
        ) {
          if (typeof window !== "undefined") {
            window.dispatchEvent(new Event("app:blocked"));
          }
          return;
        }

        if (e?.code === AxiosError.ERR_NETWORK) return;
        else if (e?.response?.status === 401) return;
        else toast.error(<ErrWarn />, { toastId: "error" });

        if (
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          ENVIRONMENT === "prod" &&
          typeof e?.response.status === "number" &&
          e.response.status >= 400
        ) {
          LogRocket.track(
            e.response.status >= 500
              ? ALLOWEAT_API_SERVER_ERROR
              : ALLOWEAT_API_ERROR,
          );
        }
      },
      retry: (failureCount, error: any) => {
        if (error.code === AxiosError.ERR_NETWORK) return false;

        if (error instanceof ValidationError) {
          return false;
        }

        if (typeof error?.code === "number") {
          if (error.code >= 400) {
            return false;
          }
        }

        if (typeof error?.response.status === "number") {
          if (error.response.status >= 400) {
            return false;
          }
        }

        return failureCount < 3;
      },
    },
    mutations: {
      onError: (e: any) => {
        console.log(e);
        if (
          typeof e?.response?.status === "number" &&
          e.response.status === 403
        ) {
          if (typeof window !== "undefined") {
            window.dispatchEvent(new Event("app:blocked"));
          }
          return;
        }
        if (
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          ENVIRONMENT === "prod" &&
          typeof e?.response.status === "number" &&
          e.response.status >= 400
        ) {
          LogRocket.track(
            e.response.status >= 500
              ? ALLOWEAT_API_SERVER_ERROR
              : ALLOWEAT_API_ERROR,
          );
        }

        if (e === "Already confirmed") {
          return false;
        }
        toast.error(<ErrWarn />);
      },
    },
  },
});

window.addEventListener("vite:preloadError", () => {
  toast(<VersionToastInfo />, {
    autoClose: false,
    closeOnClick: false,
    position: "top-center",
    toastId: "update",
    draggable: false,
  });
});

createRoot(document.getElementById("root")!).render(
  <ThemeProviderWrapper>
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <QueryClientProvider client={queryClient}>
        <TourTriggerContextProvider>
          <VisibleNutrientsContextProvider>
            <App />
          </VisibleNutrientsContextProvider>
        </TourTriggerContextProvider>
        {/* <BlockedModal /> */}
        {(ENVIRONMENT ?? "") === "local" ? (
          <ReactQueryDevtools initialIsOpen={false} />
        ) : null}
      </QueryClientProvider>
      <ToastContainer
        icon={({ type }) => {
          if (type === "success") return <CheckInCircleIcon />;
          if (type === "error") return <ExclamationMarkInCircleIcon />;
          if (type === "warning")
            return <ExclamationMarkInTriangleIcon fontSize={24} />;
          if (type === "info") return <IInCircle fontSize={16} />;
        }}
      />
    </LocalizationProvider>
  </ThemeProviderWrapper>,
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
